/* eslint-disable no-use-before-define */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { StaticImage } from 'gatsby-plugin-image';

import Button from '../button/Button';
import DropdownInput from '../dropdownInput/DropdownInput';
import TextInput from '../textInput/TextInput';
import CheckboxInput from '../checkboxInput/CheckboxInput';
import BackArrow from '../../assets/icons/back_arrow.svg';
import Check from '../../assets/icons/check.svg';
import Cross from '../../assets/icons/cross.svg';

import useCheckEligibility from './useCheckEligibility';
import useEnrollmentUrl from '../../hooks/useEnrollmentUrl';

import './CheckEligibility.scss';

export default function CheckEligibility(props) {
  const { enrollmentUrl } = useEnrollmentUrl();

  const { startResult } = props;

  const {
    form,
    handleCloseButton,
    result,
    emailSubmitted,
    setResult,
    inputState,
    setInputState,
    handleCheckState,
    states,
    isStateValid,
    inputInsurance,
    setInputInsurance,
    handleCheckEligibility,
    insurances,
    insuranceSelected,
    submitEmail,
  } = useCheckEligibility();

  useEffect(() => {
    if (startResult) {
      setResult(startResult);
    }
  }, []);

  const {
    register: setFormInput,
    handleSubmit: formSubmit,
    formState: { isValid, isSubmitting },
  } = form;

  return (
    <div className="eligibility-modal">
      <Cross
        className="close"
        width="28px"
        height="28px"
        onClick={handleCloseButton}
      />
      {!startResult && result && !emailSubmitted() && (
        <BackArrow
          className="back"
          width="22px"
          height="22px"
          fill="#7A71A6"
          onClick={() => {
            setResult(null);
          }}
        />
      )}
      <div className="content">
        <div className="title">
          <h5>
            Insurance&nbsp;
            <span className="salmon">Eligibility</span>
          </h5>
          <p>
            For affordable care, Dreem Health partners with leading insurance plans.
            <br />
            <span className="highlight">Check if your insurance covers our services</span>
            {' '}
            in two steps:
          </p>
        </div>

        <div className="inputs-container">
          <DropdownInput
            value={inputState}
            valueChange={setInputState}
            validateValue={handleCheckState}
            placeholder="Enter the state you’re insured in"
            errorMessage="Please select or enter a valid state."
            dropdownList={states}
            idKey="code"
            displayedKey="name"
          />

          {isStateValid && (
            <DropdownInput
              value={inputInsurance}
              valueChange={setInputInsurance}
              placeholder="Enter your insurance provider"
              errorMessage="Please select or enter a valid insurance provider."
              enterPressed={handleCheckEligibility}
              dropdownList={insurances}
              idKey="id"
              displayedKey="displayName"
            />
          )}
        </div>

        {(isStateValid && insuranceSelected()) && (
          <Button
            text="Check eligibility"
            displayArrow
            className="blue"
            onClick={handleCheckEligibility}
          />
        )}
      </div>

      <div className={`result ${result && 'visible'}`}>
        {result === 'eligible' && (
          <>
            <StaticImage class="image" src="../../images/checkEligibility/insurance_eligibility_available.png" alt="" />

            <div className="title">
              { startResult ? (
                <h5>
                  We accept&nbsp;
                  <span className="salmon">
                    your insurance!
                  </span>
                </h5>
              ) : (
                <h5>
                  We accept most&nbsp;
                  <span className="salmon">
                    {inputInsurance}
                    &nbsp;Options!
                  </span>
                </h5>
              )}

              <p>
                Great news, we are in network with your insurance.
                Let’s get to know you better and kickstart your journey to better sleep!
              </p>
            </div>

            <Button
              text="Book consultation now"
              displayArrow
              link={enrollmentUrl}
              className="blue"
              onClick={() => {
                if (window.posthog) window.posthog.capture('cta_enroll_now', { section: 'eligibility_check' });
              }}
              target="_blank"
            />
          </>
        )}
        {result === 'notEligibleInsurance' && (
          <>
            <StaticImage className="image smaller" src="../../images/checkEligibility/insurance_eligibility_oon.png" alt="" />

            <div className="title">
              <h5>
                Your insurance isn&apos;t in our network
              </h5>
            </div>

            <div className="list-container">
              <p><strong>Continue with our self-pay option for $149:</strong></p>

              <div className="elements">
                <p>
                  <Check />
                  45-minute consultation with a sleep specialist
                </p>
                <p>
                  <Check />
                  Personalized advice to help you sleep better
                </p>
              </div>
            </div>

            <Button
              text="Book consultation now"
              displayArrow
              link={enrollmentUrl}
              className="blue"
              onClick={() => {
                if (window.posthog) window.posthog.capture('cta_enroll_now', { section: 'eligibility_check' });
              }}
              target="_blank"
            />
          </>
        )}
        {result === 'notEligibleState' && (
          <>
            <StaticImage class="image smaller hide-mobile" src="../../images/checkEligibility/insurance_eligibility_not_available.png" alt="" />

            <div className="title">
              <h5>
                We&apos;re not in your area yet,
                <br />
                but
                <span className="salmon"> we&apos;re expanding!</span>
              </h5>
              <p>
                Want to know when we arrive near you?
                <br />
                Just enter your email below:
              </p>
            </div>

            <form
              id="state-available-form"
              onSubmit={formSubmit(submitEmail)}
              className={`${isSubmitting && 'loading'}`}
            >
              <TextInput
                placeholder="Email"
                className="large"
                register={setFormInput('email', { required: true, pattern: /\S+@\S+\.\S+/ })}
              />

              <CheckboxInput register={setFormInput('terms', { required: true })}>
                I agree to the
                {' '}
                <a target="_blank" href="/legal/terms-of-use">Terms&nbsp;and&nbsp;Conditions</a>
                {' '}
                and
                {' '}
                <a target="_blank" href="/legal/privacy-notice/">Privacy&nbsp;policy</a>
              </CheckboxInput>

              <Button
                loading={isSubmitting}
                disabled={!isValid}
                displayArrow
                submitButton
                text="Notify me"
                className="blue"
              />
            </form>
          </>
        )}
        {emailSubmitted() && (
          <>
            <StaticImage className="image smaller" src="../../images/checkEligibility/did_you_know_thanks.png" alt="" />
            <h5>
              <span className="salmon">Got it!</span>
              <br />
              We’ll keep you posted.
            </h5>
          </>
        )}
      </div>
      <div className="popup">
        <Check className="check" />
        <Cross className="cross" />
        <p />
      </div>
    </div>
  );
}

CheckEligibility.defaultProps = {
  startResult: null,
};

CheckEligibility.propTypes = {
  startResult: PropTypes.string,
};
